import { type UseQueryOptions, useQuery } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

import { API_QUERY_KEY_PRICES_ASSET } from '@/constants/api-query-key-constants';
import { useApi } from '@/hooks/use-api';
import { validateApiSchema } from '@/lib/zod-helpers';
import {
  type AssetPerformanceData,
  AssetPerformanceDataSchema,
} from '@/schemas/assets/asset-performance-schema';
import { TimePeriod } from '@/types/charts/time-period-enum';
import type { TickerSymbol } from '@/types/homaio-ticker-enum';

export function useAssetsPerformanceQuery(
  period: MaybeRefOrGetter<TimePeriod> = TimePeriod.Y1,
  ticker: MaybeRefOrGetter<keyof typeof TickerSymbol>,
  options: Partial<UseQueryOptions<AssetPerformanceData, Error>> = {},
) {
  const api = useApi();
  const { locale } = useI18n();

  return useQuery<AssetPerformanceData, Error>({
    queryKey: [API_QUERY_KEY_PRICES_ASSET, period, ticker, locale],
    queryFn: async () => {
      const response = await api.get<AssetPerformanceData>(
        `/assets/${toValue(ticker)}/performance`,
        {
          period: toValue(period),
          locale: locale.value,
          sma: true,
        },
      );
      validateApiSchema(AssetPerformanceDataSchema, response.data);
      return response.data;
    },
    refetchInterval: 5 * 60 * 1000,
    ...options,
  });
}
